// Luxury Medal Frames: shared behaviour for lmf-product-hero and lmf-product.
// Gallery thumbnails, product/preview switch, live nameplate preview, character
// counters, plate finish, photo upload preview and the ship-to note.
(() => {
  const init = (root) => {
    if (root.dataset.lmfReady) return;
    root.dataset.lmfReady = 'true';

    const views = root.querySelectorAll('[data-view]');
    const tabs = root.querySelectorAll('[data-show]');
    const show = (name) => {
      views.forEach((view) => (view.hidden = view.dataset.view !== name));
      tabs.forEach((tab) => tab.setAttribute('aria-pressed', String(tab.dataset.show === name)));
      if (name === 'preview') requestAnimationFrame(() => fitAll());
    };
    tabs.forEach((tab) => tab.addEventListener('click', () => show(tab.dataset.show)));

    // image_tag drops data-* attributes, so also match the main image by its class.
    const main = root.querySelector('[data-main-image], .lmf-product__main');
    root.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (!main) return;
        main.src = thumb.dataset.src;
        if (thumb.dataset.srcset) main.srcset = thumb.dataset.srcset;
        else main.removeAttribute('srcset');
        main.alt = thumb.dataset.alt || '';
        root.querySelectorAll('[data-thumb], [data-thumb-model]').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
        show('product');
      });
    });

    // 3D model: rendered from its template on first open, using Shopify's model viewer.
    const modelThumb = root.querySelector('[data-thumb-model]');
    const modelView = root.querySelector('[data-view="model"]');
    modelThumb?.addEventListener('click', () => {
      const template = modelView?.querySelector('[data-model-template]');
      if (template) {
        modelView.append(template.content.cloneNode(true));
        template.remove();
        window.Shopify?.loadFeatures?.([
          {
            name: 'model-viewer-ui',
            version: '1.0',
            onLoad: (errors) => {
              const viewer = modelView.querySelector('model-viewer');
              if (!errors && viewer && window.Shopify?.ModelViewerUI) new window.Shopify.ModelViewerUI(viewer);
            },
          },
        ]);
      }
      root.querySelectorAll('[data-thumb], [data-thumb-model]').forEach((t) => t.setAttribute('aria-current', String(t === modelThumb)));
      show('model');
    });

    if (root.querySelector('[id^="ProductModelJSON-"]')) {
      const setupXR = () => {
        if (!window.ShopifyXR) {
          document.addEventListener('shopify_xr_initialized', setupXR, { once: true });
          return;
        }
        root.querySelectorAll('[id^="ProductModelJSON-"]').forEach((el) => {
          window.ShopifyXR.addModels(JSON.parse(el.textContent));
          el.remove();
        });
        window.ShopifyXR.setupXRElements();
      };
      window.Shopify?.loadFeatures?.([{ name: 'shopify-xr', version: '1.0', onLoad: (errors) => !errors && setupXR() }]);
    }

    // Nameplate colours that are real product options pick the matching variant.
    const variantsEl = root.querySelector('[data-lmf-variants]');
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];
    const selectVariant = () => {
      const chosen = [];
      root.querySelectorAll('[data-option-input]:checked').forEach((input) => {
        chosen[Number(input.dataset.optionInput)] = input.value;
      });
      const match = variants.find((v) => chosen.every((value, i) => value === undefined || v.options[i] === value));
      const idInput = root.querySelector('[data-variant-id]');
      const cta = root.querySelector('[data-cta]');
      if (!match || !idInput || !cta) return;
      idInput.value = match.id;
      cta.disabled = !match.available;
      cta.textContent = match.available ? cta.dataset.ctaLabel + ' ' + match.price : cta.dataset.soldOut;
      root.querySelectorAll('[data-add-to-cart], [data-sticky-cta-button]').forEach((button) => (button.disabled = !match.available));
      const stickyButton = root.querySelector('[data-sticky-cta-button]');
      if (stickyButton) stickyButton.textContent = cta.textContent;
    };

    // "Add to cart" goes to the cart instead of checkout (keeping the discount code, if any).
    root.querySelector('[data-add-to-cart]')?.addEventListener('click', () => {
      const returnTo = root.querySelector('[data-return-to]');
      if (returnTo) returnTo.value = returnTo.dataset.cartReturn || '/cart';
    });

    // Order bar on phones: shown while the form is out of view, scrolls to the first empty required field.
    const sticky = root.querySelector('[data-sticky-cta]');
    const form = root.querySelector('.lmf-ph__form');
    if (sticky && form && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.hidden = entry.isIntersecting;
      }).observe(form);
      sticky.querySelector('[data-sticky-cta-button]')?.addEventListener('click', () => {
        const field = [...form.querySelectorAll('[required]')].find((el) => !el.value) || form.querySelector('[data-cta]');
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        // Focus only after scrolling: focusing earlier interrupts the smooth scroll.
        const focusField = () => field?.focus({ preventScroll: true });
        if (reduce || !('onscrollend' in window)) setTimeout(focusField, reduce ? 0 : 600);
        else window.addEventListener('scrollend', focusField, { once: true });
        form.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    }

    const photo = root.querySelector('[data-preview-photo]');
    const plate = root.querySelector('[data-preview-plate]');

    // Shrink the preview text until every line fits on its nameplate, like real engraving.
    const fitPlate = (el) => {
      if (!el || !el.offsetWidth) return;
      el.style.fontSize = '';
      const base = parseFloat(getComputedStyle(el).fontSize);
      let size = base;
      for (let i = 0; i < 8 && el.scrollWidth > el.clientWidth && size > base * 0.4; i++) {
        size *= Math.max(0.6, (el.clientWidth / el.scrollWidth) * 0.98);
        el.style.fontSize = size + 'px';
      }
    };
    const fitAll = () => root.querySelectorAll('[data-preview-plate]').forEach(fitPlate);
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fitAll, 150);
    });

    root.addEventListener('input', (event) => {
      const el = event.target;
      if (!el.matches('[data-line]')) return;
      const key = (el.dataset.plate || '1') + '-' + el.dataset.line;
      const count = root.querySelector('[data-count="' + key + '"]');
      if (count) count.textContent = el.value.length;
      const plateNo = el.dataset.plate || '1';
      const out =
        root.querySelector('[data-preview-line="' + plateNo + '-' + el.dataset.line + '"]') ||
        (plateNo === '1' && root.querySelector('[data-preview-line="' + el.dataset.line + '"]'));
      if (out) {
        out.textContent = el.value || el.placeholder;
        fitPlate(out.closest('[data-preview-plate]'));
      }
    });

    // Typing a nameplate switches the gallery to the live preview, so customers see the result.
    root.addEventListener('focusin', (event) => {
      if (event.target.matches('[data-line]') && root.querySelector('[data-view="preview"]')) show('preview');
    });

    root.addEventListener('change', (event) => {
      const el = event.target;
      if (el.matches('[data-finish-input]')) {
        const plateNo = el.dataset.plate || '1';
        const target = root.querySelector('[data-preview-plate="' + plateNo + '"]') || (plateNo === '1' && plate);
        if (target) target.dataset.finish = el.value.toLowerCase();
        if (root.querySelector('[data-view="preview"]')) show('preview');
      }
      if (el.matches('[data-photo-input]') && el.files && el.files[0]) {
        const name = root.querySelector('[data-photo-name]');
        if (name) name.textContent = el.files[0].name;
        if (photo) {
          if (photo.dataset.objectUrl) URL.revokeObjectURL(photo.dataset.objectUrl);
          photo.dataset.objectUrl = URL.createObjectURL(el.files[0]);
          photo.style.backgroundImage = 'url(' + photo.dataset.objectUrl + ')';
          photo.classList.add('has-photo');
          show('preview');
        }
      }
      if (el.matches('[data-option-input]')) selectVariant();
      if (el.matches('[data-ship-select]')) {
        const note = root.querySelector('[data-ship-note]');
        if (note) note.textContent = el.selectedOptions[0].dataset.note || '';
      }
    });
  };

  const run = () => document.querySelectorAll('[data-lmf-product]').forEach(init);
  run();
  document.addEventListener('shopify:section:load', run);
})();
